import * as THREE from 'three';
import type { Object3D, Intersection } from 'three';

/**
 * Configuration for collision detection
 */
export interface CollisionConfig {
  /** Radius of the camera collision sphere */
  cameraRadius: number;
  
  /** Maximum distance to check for collisions */
  maxDistance: number;
  
  /** Whether collision detection is enabled */
  enabled: boolean;

  /** Minimum distance threshold for collision calculations */
  epsilon: number;
}

/**
 * Default collision configuration
 */
export const defaultCollisionConfig: CollisionConfig = {
  cameraRadius: 0.05, // Small radius to prevent camera from getting too close
  maxDistance: 1.5,   // Check collisions up to 1.5 units ahead
  enabled: true,
  epsilon: 0.0001,
};

/**
 * Collision detection system using raycasting
 */
export class CollisionSystem {
  private raycaster: THREE.Raycaster;
  private colliders: Set<Object3D> = new Set();
  private colliderArray: Object3D[] = [];
  private colliderArrayDirty = false;
  private config: CollisionConfig;
  private normalMatrix: THREE.Matrix3;
  private lineOfSightDirection: THREE.Vector3;
  private readonly movementDirection: THREE.Vector3;
  private readonly tempVector: THREE.Vector3;
  private readonly slideDirection: THREE.Vector3;
  private readonly safePositionScratch: THREE.Vector3;
  private readonly adjustedPositionScratch: THREE.Vector3;
  private readonly slideTargetScratch: THREE.Vector3;
  private readonly contactNormalScratch: THREE.Vector3;
  private readonly directionsScratch: THREE.Vector3[];
  
  constructor(config: Partial<CollisionConfig> = {}) {
    this.config = { ...defaultCollisionConfig, ...config };
    this.raycaster = new THREE.Raycaster();
    this.normalMatrix = new THREE.Matrix3();
    this.lineOfSightDirection = new THREE.Vector3();
    this.movementDirection = new THREE.Vector3();
    this.tempVector = new THREE.Vector3();
    this.slideDirection = new THREE.Vector3();
    this.safePositionScratch = new THREE.Vector3();
    this.adjustedPositionScratch = new THREE.Vector3();
    this.slideTargetScratch = new THREE.Vector3();
    this.contactNormalScratch = new THREE.Vector3();
    this.directionsScratch = [
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(-1, 0, 0),
      new THREE.Vector3(0, 0, 1),
      new THREE.Vector3(0, 0, -1),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(0, -1, 0),
      new THREE.Vector3(1, 0, 1),
      new THREE.Vector3(-1, 0, 1),
      new THREE.Vector3(1, 0, -1),
      new THREE.Vector3(-1, 0, -1),
    ];
    for (let i = 6; i < this.directionsScratch.length; i += 1) {
      this.directionsScratch[i].normalize();
    }
  }
  
  /**
   * Add an object to the collision system
   */
  addCollider(object: Object3D): void {
    this.colliders.add(object);
    this.colliderArrayDirty = true;
  }
  
  /**
   * Remove an object from the collision system
   */
  removeCollider(object: Object3D): void {
    this.colliders.delete(object);
    this.colliderArrayDirty = true;
  }
  
  /**
   * Clear all colliders
   */
  clearColliders(): void {
    this.colliders.clear();
    this.colliderArrayDirty = true;
  }

  private getColliderArray(): Object3D[] {
    if (!this.colliderArrayDirty) {
      return this.colliderArray;
    }
    this.colliderArray.length = 0;
    this.colliders.forEach((collider) => {
      this.colliderArray.push(collider);
    });
    this.colliderArrayDirty = false;
    return this.colliderArray;
  }
  
  /**
   * Check if a position would collide with any collider
   * @param position The position to check
   * @param direction Optional direction vector for more accurate checking
   * @returns true if collision detected, false otherwise
   */
  checkCollision(position: THREE.Vector3, direction?: THREE.Vector3): boolean {
    const colliders = this.getColliderArray();
    if (!this.config.enabled || colliders.length === 0) {
      return false;
    }
    
    // Cast rays in multiple directions to check for collisions
    if (direction) {
      this.movementDirection.copy(direction).normalize();
      this.raycaster.set(position, this.movementDirection);
      const intersects = this.raycaster.intersectObjects(colliders, true);
      for (const intersect of intersects) {
        if (intersect.distance < this.config.cameraRadius + this.config.maxDistance) {
          return true;
        }
      }
      return false;
    }

    for (let i = 0; i < this.directionsScratch.length; i += 1) {
      const dir = this.directionsScratch[i];
      this.raycaster.set(position, dir);
      const intersects = this.raycaster.intersectObjects(colliders, true);
      
      // Check if any intersection is within the camera radius + max distance
      for (const intersect of intersects) {
        const distance = intersect.distance;
        if (distance < this.config.cameraRadius + this.config.maxDistance) {
          return true;
        }
      }
    }
    
    return false;
  }

  /**
   * Determines if any collider obstructs the line of sight between two points
   * @param start Line origin (e.g. camera position)
   * @param target Target position (e.g. sight center)
   * @param padding Distance near the target that should be ignored to avoid self-intersection
   */
  isLineOfSightObstructed(
    start: THREE.Vector3,
    target: THREE.Vector3,
    padding: number = Math.max(this.config.cameraRadius, this.config.epsilon * 10)
  ): boolean {
    const colliders = this.getColliderArray();
    if (!this.config.enabled || colliders.length === 0) {
      return false;
    }

    const direction = this.lineOfSightDirection.subVectors(target, start);
    const distance = direction.length();

    if (distance === 0) {
      return false;
    }

    const targetPadding = Math.max(padding, this.config.epsilon);

    if (distance <= targetPadding) {
      return false;
    }

    direction.normalize();

    const previousNear = this.raycaster.near;
    const previousFar = this.raycaster.far;
    const occlusionDistance = Math.max(distance - targetPadding, this.config.epsilon);

    this.raycaster.set(start, direction);
    this.raycaster.near = 0;
    this.raycaster.far = occlusionDistance;

    const intersects = this.raycaster.intersectObjects(colliders, true);

    this.raycaster.near = previousNear;
    this.raycaster.far = previousFar;

    for (const intersect of intersects) {
      if (intersect.distance <= occlusionDistance) {
        return true;
      }
    }

    return false;
  }
  
  /**
   * Check if movement from current position to new position would cause a collision
   * @param currentPosition Current camera position
   * @param newPosition Proposed new camera position
   * @returns true if movement would cause collision, false otherwise
   */
  checkMovement(currentPosition: THREE.Vector3, newPosition: THREE.Vector3): boolean {
    const colliders = this.getColliderArray();
    if (!this.config.enabled || colliders.length === 0) {
      return false;
    }
    
    // Calculate movement direction
    this.movementDirection.subVectors(newPosition, currentPosition);
    const direction = this.movementDirection;
    const distance = direction.length();
    
    if (distance === 0) {
      return false;
    }
    
    direction.normalize();
    
    // Cast a ray from current position in the movement direction
    this.raycaster.set(currentPosition, direction);
    const intersects = this.raycaster.intersectObjects(colliders, true);
    
    // Check if any intersection is too close
    for (const intersect of intersects) {
      const intersectDistance = intersect.distance;
      // If intersection is closer than the movement distance + camera radius, collision!
      if (intersectDistance < distance + this.config.cameraRadius) {
        return true;
      }
    }
    
    // Also check the new position itself
    return this.checkCollision(newPosition, direction);
  }
  
  /**
   * Get a safe position by sliding along collision surfaces
   * @param currentPosition Current camera position
   * @param desiredPosition Desired new position
   * @returns Safe position that doesn't collide, or current position if no safe position found
   */
  getSafePosition(
    currentPosition: THREE.Vector3,
    desiredPosition: THREE.Vector3,
    recursionDepth: number = 0
  ): THREE.Vector3 {
    const colliders = this.getColliderArray();
    if (!this.config.enabled || colliders.length === 0) {
      return desiredPosition;
    }
    if (recursionDepth > 3) {
      return currentPosition;
    }
    
    this.tempVector.subVectors(desiredPosition, currentPosition);
    const direction = this.tempVector;
    const distance = direction.length();
    
    if (distance === 0) {
      return currentPosition;
    }
    
    const movementDir = this.movementDirection.copy(direction).normalize();
    
    // Cast ray to find collision point
    this.raycaster.set(currentPosition, movementDir);
    const intersects = this.raycaster.intersectObjects(colliders, true);
    
    // If no intersection, check if the desired position itself is safe
    if (intersects.length === 0) {
      if (!this.checkCollision(desiredPosition, movementDir)) {
        return desiredPosition;
      }
      // If desired position has collision, return current position
      return currentPosition;
    }
    
    const firstIntersect = intersects[0];
    
    // If collision is beyond the desired position, it's safe
    if (firstIntersect.distance > distance + this.config.cameraRadius) {
      return desiredPosition;
    }
    
    // Collision detected - stop just before it
    const epsilon = this.config.epsilon;
    const safeDistance = Math.max(0, firstIntersect.distance - this.config.cameraRadius);
    let safePosition = this.safePositionScratch.copy(currentPosition);
    if (safeDistance > epsilon) {
      safePosition.addScaledVector(movementDir, safeDistance);
    }
    let safePositionHasCollision = this.checkCollision(safePosition);

    // Try sliding along the surface if there is remaining distance
    const contactNormal = this.getIntersectionWorldNormal(firstIntersect);
    const clippedInSurface = safeDistance < this.config.cameraRadius * 0.2;
    if (safePositionHasCollision && contactNormal) {
      const offset = clippedInSurface ? Math.min(this.config.cameraRadius * 0.5, 0.01) : 0;
      if (offset > 0) {
        const adjusted = this.adjustedPositionScratch
          .copy(safePosition)
          .addScaledVector(contactNormal, offset);
        if (!this.checkCollision(adjusted)) {
          safePosition = adjusted;
          safePositionHasCollision = false;
        }
      }
    }

    const remainingDistance = Math.max(0, distance - safeDistance);
    
    if (contactNormal && remainingDistance > 0.001) {
      this.slideDirection.copy(movementDir).addScaledVector(contactNormal, -movementDir.dot(contactNormal));
      if (this.slideDirection.lengthSq() > epsilon * epsilon) {
        this.slideDirection.normalize();
        const slideTarget = this.slideTargetScratch
          .copy(safePosition)
          .addScaledVector(this.slideDirection, remainingDistance);
        const slideResult = this.getSafePosition(safePosition, slideTarget, recursionDepth + 1);
        if (!slideResult.equals(safePosition)) {
          return slideResult;
        }
      }
    }
    
    if (!safePositionHasCollision && safePosition.distanceTo(currentPosition) > epsilon) {
      return safePosition;
    }
    
    return currentPosition;
  }
  
  /**
   * Update collision configuration
   */
  updateConfig(config: Partial<CollisionConfig>): void {
    this.config = { ...this.config, ...config };
  }
  
  /**
   * Get current configuration
   */
  getConfig(): CollisionConfig {
    return { ...this.config };
  }

  private getIntersectionWorldNormal(intersect: Intersection<Object3D>): THREE.Vector3 | null {
    if (!intersect.face) {
      return null;
    }
    const normal = this.contactNormalScratch.copy(intersect.face.normal);
    this.normalMatrix.getNormalMatrix(intersect.object.matrixWorld);
    normal.applyMatrix3(this.normalMatrix).normalize();
    return normal;
  }
}
